import React, { useState } from 'react';
import {
    Box,
    Typography,
    Collapse,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Switch,
    FormControlLabel
} from '@mui/material';
import {
    ExpandMore,
    ExpandLess,
    Error,
    ErrorOutline,
    CheckCircle
} from '@mui/icons-material';

const IssueIcon = ({ hasIssues, issueCount }) => {
    if (hasIssues) {
        return <Error color="error" sx={{ fontSize: 16 }} />;
    }
    return <CheckCircle color="success" sx={{ fontSize: 16 }} />;
};

const SectionItem = ({ section, level = 0, showOnlyIssues = true }) => {
    // Default to collapsed if section has no issues, expanded if it has issues
    const [expanded, setExpanded] = useState(level < 1 && !section.is_valid);
    const hasSubsections = section.sections && Object.keys(section.sections).length > 0;
    const hasIssues = !section.is_valid;
    const issues = section.issues_by_code_invalid ? Object.values(section.issues_by_code_invalid) : [];

    const handleToggle = () => {
        setExpanded(!expanded);
    };

    // Filter subsections based on showOnlyIssues setting
    const getFilteredSubsections = () => {
        if (!hasSubsections) return [];
        
        const subsections = Object.values(section.sections);
        
        // If showing all, return all subsections
        if (!showOnlyIssues) {
            return subsections;
        }
        
        // If showing only issues, filter subsections that have issues
        return subsections.filter(subsection => !subsection.is_valid);
    };

    const filteredSubsections = getFilteredSubsections();
    const hasFilteredSubsections = filteredSubsections.length > 0;
    const shouldShowExpandButton = hasSubsections && (showOnlyIssues ? hasFilteredSubsections : true);

    // Don't render this section if it has no issues and we're only showing issue items
    if (showOnlyIssues && !hasIssues && !hasFilteredSubsections) {
        return null;
    }

    return (
        <Box>
            <ListItem
                sx={{
                    py: 0.25,
                    minHeight: 32,
                    pl: level * 2 + 1,
                    '&:hover': {
                        backgroundColor: 'action.hover'
                    }
                }}
            >
                <ListItemIcon sx={{ minWidth: 32 }}>
                    <IssueIcon hasIssues={hasIssues} issueCount={issues.length} />
                </ListItemIcon>
                
                <ListItemText
                    primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: hasIssues ? 500 : 400 }}>
                                {section.section_name || section.section}
                            </Typography>
                            {hasIssues && issues.length > 0 && (
                                <Chip
                                    label={`${issues.length} issue${issues.length !== 1 ? 's' : ''}`}
                                    size="small"
                                    color="error"
                                    sx={{ height: 16, fontSize: '0.65rem' }}
                                />
                            )}
                        </Box>
                    }
                    secondary={
                        hasIssues && issues.length > 0 && (
                            <Box sx={{ mt: 0.25 }}>
                                {issues.slice(0, 2).map((issue, index) => (
                                    <Typography
                                        key={index}
                                        variant="caption"
                                        color="error"
                                        sx={{ display: 'block', fontSize: '0.7rem' }}
                                    >
                                        • {issue.readable || issue.code}
                                    </Typography>
                                ))}
                                {issues.length > 2 && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ fontSize: '0.7rem' }}
                                    >
                                        +{issues.length - 2} more issues
                                    </Typography>
                                )}
                            </Box>
                        )
                    }
                />
                
                {shouldShowExpandButton && (
                    <IconButton
                        size="small"
                        onClick={handleToggle}
                        sx={{ ml: 1 }}
                    >
                        {expanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                )}
            </ListItem>
            
            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <List dense sx={{ py: 0 }}>
                    {filteredSubsections.map((subsection, index) => (
                        <SectionItem
                            key={`${subsection.section_name || subsection.section || index}`}
                            section={subsection}
                            level={level + 1}
                            showOnlyIssues={showOnlyIssues}
                        />
                    ))}
                </List>
            </Collapse>
        </Box>
    );
};

// Recursively count all issues in the tree
const countAllIssues = (section) => {
    let count = 0;
    
    // Count issues in current section
    if (section.issues_by_code_invalid) {
        count += Object.keys(section.issues_by_code_invalid).length;
    }
    
    // Recursively count issues in subsections
    if (section.sections) {
        Object.values(section.sections).forEach(subsection => {
            count += countAllIssues(subsection);
        });
    }
    
    return count;
};

const FlowIssues = ({ issues, hideSidebar }) => {
    const [showOnlyIssues, setShowOnlyIssues] = useState(true);

    if (!issues) {
        return (
            <div className='flow-sidebar' style={{width: '15vw'}}>
                <Box className='flow-sidebar__header'>
                    <Typography component='div' variant='body1' className='flow-sidebar_title no-edit'>
                        <ErrorOutline/>
                        Flow Issues
                    </Typography>
                </Box>
                <Box className='flow-sidebar__body'>
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ p: 2 }}>
                        No issues data available
                    </Typography>
                </Box>
            </div>
        );
    }

    const hasIssues = !issues.is_valid;
    const issueCount = countAllIssues(issues);

    return (
        <div className='flow-sidebar' style={{width: '15vw'}}>
            <Box className='flow-sidebar__header'>
                <Typography component='div' variant='body1' className='flow-sidebar_title no-edit'>
                    <ErrorOutline/>
                    Flow Issues
                </Typography>
            </Box>
            <Box className='flow-sidebar__body'>
                <Box sx={{ p: 2 }}>
                    <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
                        <IssueIcon hasIssues={hasIssues} issueCount={issueCount} />
                        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {hasIssues ? (
                                <>
                                    Flow has 
                                    <Chip
                                        label={`${issueCount} issue${issueCount !== 1 ? 's' : ''}`}
                                        size="small"
                                        color="error"
                                        sx={{ height: 20, fontSize: '0.75rem' }}
                                    />
                                    that need attention
                                </>
                            ) : (
                                'No issues found'
                            )}
                        </Typography>
                        {hasIssues && (
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={!showOnlyIssues}
                                        onChange={(e) => setShowOnlyIssues(!e.target.checked)}
                                        size="small"
                                    />
                                }
                                label="Show All"
                                sx={{ ml: 1 }}
                            />
                        )}
                    </Box>

                    {hasIssues && issues.issues_by_code_invalid && Object.keys(issues.issues_by_code_invalid).length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'medium' }}>
                                Issue Summary
                            </Typography>
                            <Box>
                                {Object.values(issues.issues_by_code_invalid).slice(0, 5).map((issue, index) => (
                                    <Chip
                                        key={index}
                                        label={issue.readable || issue.code}
                                        size="small"
                                        variant="outlined"
                                        color="error"
                                        sx={{ mr: 0.5, mb: 0.5 }}
                                    />
                                ))}
                                {Object.keys(issues.issues_by_code_invalid).length > 5 && (
                                    <Chip
                                        label={`+${Object.keys(issues.issues_by_code_invalid).length - 5} more`}
                                        size="small"
                                        variant="outlined"
                                        color="error"
                                        sx={{ mr: 0.5, mb: 0.5 }}
                                    />
                                )}
                            </Box>
                        </Box>
                    )}

                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'medium' }}>
                        Section Details
                    </Typography>
                    
                    <List dense sx={{ py: 0 }}>
                        {issues.sections && Object.entries(issues.sections).map(([key, section]) => (
                            <SectionItem
                                key={key}
                                section={section}
                                level={0}
                                showOnlyIssues={showOnlyIssues}
                            />
                        ))}
                    </List>
                </Box>
            </Box>
        </div>
    );
};

export default FlowIssues;
